// features/projects/projects-panel.js
// ═══════════════════════════════════════════════════════════
// PROJECTS PANEL — card grid using the shared ShiftCard.
//
// Layout matches Space panel exactly:
//   - ShiftPanelHeader (title + tagline + tabs + actions + close)
//   - Active | Finished tabs
//   - Card grid (uses ShiftCard for visual consistency)
//   - Empty states per tab
//
// + New Project opens NewProjectModal. Mark Finished / Reopen
// rendered in card overflow (left of delete) — kept lightweight.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import ShiftPanelHeader from '../../components/shift-panel-header';
import ShiftCard from '../../components/shift-card';
import NewProjectModal from './new-project-modal';

export default function ProjectsPanel({ userId, userRecord, onClose, onOpenProject }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [activeTab, setActiveTab] = useState('Active');
  const [searchQuery, setSearchQuery] = useState('');

  const loadProjects = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/projects?user_id=${encodeURIComponent(userId)}&include_archived=true`,
        { cache: 'no-store' },
      );
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (err) {
      console.warn('[projects-panel] load failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  async function updateProjectStatus(id, newStatus) {
    try {
      const res = await fetch('/api/projects', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, user_id: userId, status: newStatus }),
      });
      if (res.ok) loadProjects();
    } catch (err) {
      console.warn('[projects-panel] update failed:', err.message);
    }
  }

  // Filter by tab
  const activeProjects = projects.filter((p) => p.status === 'active');
  const finishedProjects = projects.filter(
    (p) => p.status === 'done' || p.status === 'paused',
  );
  const tabProjects = activeTab === 'Active' ? activeProjects : finishedProjects;

  // Filter by search query (title + description)
  const visibleProjects = !searchQuery.trim()
    ? tabProjects
    : tabProjects.filter((p) => {
        const q = searchQuery.toLowerCase();
        return (p.title || '').toLowerCase().includes(q)
          || (p.description || '').toLowerCase().includes(q);
      });

  return (
    <div style={S.root}>
      <ShiftPanelHeader
        title="Projects"
        tagline="Where curiosity becomes a build."
        taglineInline
        tabs={[
          { label: 'Active',   color: '#c8a96e' },
          { label: 'Finished', color: '#9b8fc4' },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        actionButtons={[
          { label: '+ New Project', color: '#7a9e82', onClick: () => setShowNew(true) },
        ]}
        onClose={onClose}
      />

      {/* Search toolbar — same classes as Space panel for visual parity */}
      <div className="cards-toolbar" style={{ borderBottom: '1px solid var(--border-1)', background: 'var(--surface-1)', flexShrink: 0 }}>
        <div className="cards-search-wrap">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="5" stroke="var(--text-5)" strokeWidth="1.4"/>
            <path d="M10.5 10.5L14 14" stroke="var(--text-5)" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="cards-search"
            placeholder="Search projects…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div style={S.body}>
        {loading && <div style={S.empty}>Loading…</div>}

        {!loading && visibleProjects.length === 0 && (
          searchQuery.trim() ? (
            <div style={S.empty}>No projects match &ldquo;{searchQuery}&rdquo;</div>
          ) : (
            <EmptyState tab={activeTab} onNew={() => setShowNew(true)} />
          )
        )}

        {!loading && visibleProjects.length > 0 && (
          <div className="cards-container">
          <div className="cards-grid">
            {visibleProjects.map((p) => {
              const isFinished = p.status === 'done' || p.status === 'paused';
              const accentColor = p.type === 'custom' ? '#9b8fc4' : '#7a9e82';
              const typeLabel = p.type === 'custom' ? 'CUSTOM' : 'SIMPLE';
              const cardItem = {
                id: p.id,
                title: p.title,
                description: p.description || '',
                created_at: p.created_at,
                last_used_at: p.last_activity_at,
                summary_bullets: [],
              };
              return (
                <ShiftCard
                  key={p.id}
                  item={cardItem}
                  accentColor={accentColor}
                  typeLabel={typeLabel}
                  preview={p.description || (isFinished ? 'Paused or completed.' : 'No description yet.')}
                  actionLabel="Open →"
                  onAction={() => onOpenProject?.(p)}
                  secondaryActionLabel={isFinished ? 'Reopen' : 'Finished'}
                  secondaryActionColor={isFinished ? '#9b8fc4' : '#7a9e82'}
                  onSecondaryAction={() => updateProjectStatus(p.id, isFinished ? 'active' : 'done')}
                />
              );
            })}
          </div>
          </div>
        )}
      </div>

      {showNew && (
        <NewProjectModal
          userId={userId}
          userRecord={userRecord}
          onClose={() => setShowNew(false)}
          onCreated={(newProject) => {
            setShowNew(false);
            loadProjects();
            // Drop the user straight into the projects 3-panel chat
            if (newProject) onOpenProject?.(newProject);
          }}
        />
      )}
    </div>
  );
}

function EmptyState({ tab, onNew }) {
  if (tab === 'Finished') {
    return (
      <div style={S.emptyState}>
        <div style={S.emptyTitle}>No finished projects yet.</div>
        <div style={S.emptyText}>
          When you complete or pause a project, it moves here. You can reopen
          it anytime — past work stays available.
        </div>
      </div>
    );
  }
  return (
    <div style={S.emptyState}>
      <div style={S.emptyTitle}>Start your first project.</div>
      <div style={S.emptyText}>
        A project is a bounded experiment. Build a robot, run a research
        study, learn how to make water from thin air. Anything you want to
        try with Shift's help.
      </div>
      <button style={S.emptyBtn} onClick={onNew}>+ New Project</button>
    </div>
  );
}

const S = {
  root: {
    height: '100%',
    background: 'var(--surface-1, #0c0c11)',
    color: 'var(--text, #e8e8f0)',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 0 48px',
  },
  empty: { color: '#555', fontSize: 13, padding: 40, textAlign: 'center' },
  emptyState: {
    maxWidth: 480,
    margin: '80px auto 0',
    textAlign: 'center',
  },
  emptyTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 22,
    fontWeight: 'bold',
    color: '#c8a96e',
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 13,
    color: '#7a807e',
    lineHeight: 1.7,
    marginBottom: 24,
  },
  emptyBtn: {
    background: 'rgba(122,158,130,.12)',
    border: '1px solid rgba(122,158,130,.4)',
    color: '#7a9e82',
    padding: '10px 22px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'Georgia, serif',
  },
};
